import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { MarketHelpConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { MarketView, WinGameFixture } from '@frontend/sports/racing/feature/models';

import { HorsesFixtureHelper } from '../../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from '../../../../horse-racing-shared/services/horses-fixture-market-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { HorsesFixturePricingHelper } from '../../../../horse-racing-shared/services/horses-fixture-pricing-helper.service';
import { HorsesFixtureTranslationHelper } from '../../../../horse-racing-shared/services/horses-fixture-translation-helper.service';
import { UrlHelperService } from '../../../../navigation-core/url-helper.service';
import { EachWayMarketHelperService } from '../../../../option-group/each-way-market-helper.service';
import { TabBarItem } from '../../../../tab-bar/tab-bar.models';
import { GreyhoundTrackingService } from '../../../common/greyhound-tracking.service';
import { RacingService } from '../../../services/racing-service';
import { RaceComponent } from './race-base.component';

@Component({
    selector: 'ms-greyhounds-race',
    templateUrl: './greyhounds-race.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class GreyhoundsRaceComponent extends RaceComponent implements OnChanges {
    @Input() displaySorting = true;
    @Input() trackingSource: string;
    @Input() isNextRaces: boolean;
    readonly sportTracking = 'Greyhounds';
    isRacingTerminal = false;
    isForecastAvailable = false;
    isTricastAvailable = false;
    trackingOptions: { [key: string]: string };

    @Input() greyhoundTrapDogMapping = {};

    constructor(
        public sitecore: Sitecore,
        protected override horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
        protected override horsesFixturePricingHelper: HorsesFixturePricingHelper,
        protected override greyhoundTrackingService: GreyhoundTrackingService,
        protected override horsesFixtureHelper: HorsesFixtureHelper,
        protected override horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected override horsesFixtureTranslationHelper: HorsesFixtureTranslationHelper,
        protected override eachWayMarketHelperService: EachWayMarketHelperService,
        protected racingService: RacingService,
        protected override marketHelpConfig: MarketHelpConfig,
        protected override urlHelper: UrlHelperService,
    ) {
        super(
            horsesFixtureMarketHelper,
            horsesFixtureTranslationHelper,
            eachWayMarketHelperService,
            horsesFixtureHelper,
            horsesFixtureParticipantHelper,
            horsesFixturePricingHelper,
            greyhoundTrackingService,
            marketHelpConfig,
            urlHelper,
        );
    }

    ngOnChanges(changes: ISimpleChanges<GreyhoundsRaceComponent>): void {
        if (changes.fixture && changes.fixture.currentValue) {
            const resetSelectedMarket = changes.fixture.firstChange || changes.fixture.previousValue.id !== changes.fixture.currentValue.id;
            this.isOptionBet = changes.fixture.currentValue.isOptionBet;
            this.setViewModel(changes.fixture.currentValue, resetSelectedMarket);
        }
    }

    setViewModel(fixture: WinGameFixture, resetSelectedMarket: boolean): void {
        this.marketTabs = this.horsesFixtureMarketHelper.getMarketsTabs(fixture);
        if (resetSelectedMarket) {
            // When switch to another fixture always select first available market.
            this.selectedMarketTitle = this.getFirstMarketTitleIfAny();
        } else {
            // When fixture is updated, keep current market selected.
            const currentSelectedMarket = this.selectedMarketTitle && this.marketTabs.find((t) => t.title === this.selectedMarketTitle);
            this.selectedMarketTitle = currentSelectedMarket ? currentSelectedMarket.title : this.getFirstMarketTitleIfAny();
        }
    }

    get active(): TabBarItem {
        return (this.marketTabs && this.marketTabs.filter((tab) => tab.active).pop()) ?? this.marketTabs[0];
    }

    setSelectedMarket(activeTabId: number) {
        this.selectedMarketTitle = this.marketTabs.find((tab) => tab.id === activeTabId)?.title ?? '';
        this.greyhoundTrackingService.trackselectMarket(this.fixture, this.selectedMarketTitle);
    }

    isWinMarketSelected(selectedMarketTitle: Nullable<MarketView> | string, market: string): boolean {
        //below cast is just a dummy cast to avoid any index error
        let translatedWinMarketTitle = this.horsesFixtureTranslationHelper.translateMarketView(MarketView[market as 'Win']);
        return selectedMarketTitle === translatedWinMarketTitle;
    }

    getPickTrackingOptions(): { [key: string]: string } {
        const trackingSource = this.getPickTrackingSource(this.sportTracking);
        return this.greyhoundTrackingService.getAdditionalTrackingOptions(this.fixture, trackingSource);
    }
}
